import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X } from 'lucide-react'
import { carePlans } from '../data/carePlans'
import { specialties } from '../data/specialties'

const FloatingSearchBox = ({ isVisible, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [activeSuggestion, setActiveSuggestion] = useState(-1)
  const [isExpanded, setIsExpanded] = useState(false)
  const navigate = useNavigate()
  const searchRef = useRef(null)
  const inputRef = useRef(null)

  // Generate suggestions based on search term
  useEffect(() => {
    if (searchTerm.trim().length > 1) {
      const lowercaseQuery = searchTerm.toLowerCase()
      const results = []

      // Search care plans
      const matchingCarePlans = carePlans.filter(plan =>
        plan.title.toLowerCase().includes(lowercaseQuery) ||
        plan.description.toLowerCase().includes(lowercaseQuery) ||
        plan.category.toLowerCase().includes(lowercaseQuery)
      ).slice(0, 5).map(plan => ({
        ...plan,
        type: 'care-plan',
        displayName: plan.title
      }))

      // Search specialties
      const matchingSpecialties = specialties.filter(specialty =>
        specialty.name.toLowerCase().includes(lowercaseQuery) ||
        specialty.description.toLowerCase().includes(lowercaseQuery)
      ).slice(0, 3).map(specialty => ({
        ...specialty,
        type: 'specialty',
        displayName: specialty.name
      }))

      // Combine results
      results.push(...matchingCarePlans, ...matchingSpecialties)

      // Sort by relevance
      results.sort((a, b) => {
        const aName = a.displayName.toLowerCase()
        const bName = b.displayName.toLowerCase()
        const aExact = aName.startsWith(lowercaseQuery)
        const bExact = bName.startsWith(lowercaseQuery)

        if (aExact && !bExact) return -1
        if (!aExact && bExact) return 1
        return aName.localeCompare(bName)
      })

      setSuggestions(results.slice(0, 6))
      setShowSuggestions(true)
    } else {
      setSuggestions([])
      setShowSuggestions(false)
    }
    setActiveSuggestion(-1)
  }, [searchTerm])

  // Handle form submission
  const handleSearch = (e) => {
    e.preventDefault()
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`)
      handleClose()
    }
  }

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'care-plan') {
      navigate(`/care-plan/${suggestion.id}`)
    } else {
      navigate(`/specialty/${suggestion.id}`)
    }
    handleClose()
  }

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveSuggestion(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveSuggestion(prev => prev > 0 ? prev - 1 : -1)
        break
      case 'Enter':
        e.preventDefault()
        if (activeSuggestion >= 0) {
          handleSuggestionClick(suggestions[activeSuggestion])
        } else {
          handleSearch(e)
        }
        break
      case 'Escape':
        handleClose()
        break
    }
  }

  const handleClose = () => {
    setSearchTerm('')
    setShowSuggestions(false)
    setActiveSuggestion(-1)
    setIsExpanded(false)
    if (onClose) onClose()
  }

  const handleExpand = () => {
    setIsExpanded(true)
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, 100)
  }

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false)
        setActiveSuggestion(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Add/remove body class for padding when floating search is expanded
  useEffect(() => {
    if (isVisible && isExpanded) {
      document.body.classList.add('floating-search-active')
    } else {
      document.body.classList.remove('floating-search-active')
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('floating-search-active')
    }
  }, [isVisible, isExpanded])

  if (!isVisible) return null

  return (
    <div className={`floating-search-container ${isExpanded ? 'expanded' : ''}`}>
      <div className="floating-search-content" ref={searchRef}>
        {!isExpanded ? (
          // Collapsed state - just the search icon
          <button 
            className="floating-search-trigger"
            onClick={handleExpand}
            aria-label="Open search"
          >
            <Search size={20} />
          </button>
        ) : (
          // Expanded state - full search form
          <>
            <form onSubmit={handleSearch} className="floating-search-form">
              <div className="floating-search-input-container">
                <Search size={18} className="floating-search-icon" />
                <input
                  ref={inputRef}
                  type="text"
                  className="floating-search-input"
                  placeholder="Search care plans..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                />
                <button
                  type="button"
                  className="floating-search-close"
                  onClick={handleClose}
                  aria-label="Close search"
                >
                  <X size={18} />
                </button>
              </div>
            </form>

            {/* Suggestions */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="floating-search-suggestions">
                {suggestions.map((suggestion, index) => (
                  <div
                    key={`${suggestion.type}-${suggestion.id}`}
                    className={`floating-search-suggestion ${index === activeSuggestion ? 'active' : ''}`}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <div className="suggestion-title">{suggestion.displayName}</div>
                    <div className="suggestion-category">
                      {suggestion.type === 'care-plan' ? suggestion.category : 'Specialty'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default FloatingSearchBox 